import {
  PayoutBatchClosedReason,
  type PayoutBatchClosedReasonValue,
  PayoutBatchStatus,
} from '@fluenceur/types';

/**
 * The two states the batch status alone cannot express. `fundedAt` is the only
 * record that the brand's money arrived, so the dashboard must read it rather
 * than trust a status that a refused first attempt may have left behind.
 */
export const PAYMENT_DECLINED = 'payment_declined';
export const CHARGED_TRANSFERS_INCOMPLETE = 'charged_transfers_incomplete';

/**
 * A payout the brand never paid, closed at the deadline its reminder announced.
 * It is `cancelled` like every other closed payout, but the brand did not
 * cancel it, so it must not read as if it had.
 */
export const EXPIRED = 'expired';

export type PayoutDisplayState =
  | PayoutBatchStatus
  | typeof PAYMENT_DECLINED
  | typeof CHARGED_TRANSFERS_INCOMPLETE
  | typeof EXPIRED;

export interface PayoutFundingFacts {
  status: PayoutBatchStatus;
  /** Instant the brand's payment was accepted, null while it was not. */
  fundedAt: string | null;
  /** Instant of the last refused payment attempt, null when none was refused. */
  lastPaymentFailedAt: string | null;
  /** Why an unpaid payout was closed, null or absent while it is open or was paid. */
  closedReason?: PayoutBatchClosedReasonValue | null;
}

/**
 * The single decision behind every payout rendering: badge, row actions, drawer.
 *
 * Money arriving outranks the status because the two can disagree. A refused
 * attempt followed by a successful retry on the same payment leaves batches
 * whose status still reads "failed" while the brand was charged: rendering that
 * as a failure tells the brand its money is safe when it is not.
 */
export function resolvePayoutDisplayState({
  status,
  fundedAt,
  lastPaymentFailedAt,
  closedReason = null,
}: PayoutFundingFacts): PayoutDisplayState {
  if (fundedAt) {
    // Charged, yet the transfers to the affiliates did not all go through.
    if (status === PayoutBatchStatus.FAILED || status === PayoutBatchStatus.PARTIALLY_COMPLETED) {
      return CHARGED_TRANSFERS_INCOMPLETE;
    }

    return status;
  }

  if (status === PayoutBatchStatus.CANCELLED && closedReason === PayoutBatchClosedReason.EXPIRED) {
    return EXPIRED;
  }

  // Nothing was charged and the batch stays payable: the brand can pay again.
  if (lastPaymentFailedAt && status === PayoutBatchStatus.DRAFT) {
    return PAYMENT_DECLINED;
  }

  return status;
}

/**
 * Whether the brand can still take this batch to payment, which is the retry
 * path after a refusal: a declined batch is a draft that carries a refusal.
 */
export function isPayoutResumable(state: PayoutDisplayState): boolean {
  return state === PayoutBatchStatus.DRAFT || state === PAYMENT_DECLINED;
}
